/**
 * Commission Auto-Record
 *
 * Records one commission ledger row per deal, at whichever of two events
 * happens first: the deal moving to Executed, or its contract going active.
 *
 * Until Sep 2026 each event kept its own duplicate check (by agent+deal on
 * one side, agent+contract on the other), so the same deal was recorded
 * twice and every ledger total doubled. The check is now agent-agnostic and
 * looks for a row against the deal OR the contract, whichever is known.
 *
 * Expenses deducted from a commission are now marked recouped on the expense
 * itself; before, `recouped_amount` never advanced, so the same expense was
 * subtracted from every later commission forever.
 */

import {
  recordCommissionEarned,
  recordContractCommissionEarned,
  recoupExpenseFromDeal,
  getAgencyCommissionSettings,
  getAgentCommissionSplit,
  listAgentExpenses,
  listCommissionLedger,
} from '../api/commissions'
import {
  getEffectiveSplit,
  reconciledAmounts,
  sameMoney,
} from './commissionCalculations'
import { unrecoupedTotal, newLedgerAmounts, recoupmentPlan, contractBasis, dealBasis, grossCommission } from '../shared/commissionModel'
import { updateCommissionLedgerEntry } from '../api/commissions'
import { localDateString } from './formatters'
import type { Deal } from '../types'
import type { AgentExpense } from '../types/commission'
import type { Contract } from '../types/contract'

/** A commission_earned row already exists for this deal or this contract. */
export async function hasCommissionEntry(ids: { dealId?: string | null; contractId?: string | null }): Promise<boolean> {
  const checks: Promise<unknown[]>[] = []
  if (ids.dealId) checks.push(listCommissionLedger({ dealId: ids.dealId, transactionType: 'commission_earned' }))
  if (ids.contractId) checks.push(listCommissionLedger({ contractId: ids.contractId, transactionType: 'commission_earned' }))
  if (checks.length === 0) return false
  const results = await Promise.all(checks)
  return results.some(rows => rows.length > 0)
}

/** Kept for existing callers. */
export async function hasDealCommissionEntry(dealId: string, _agentId?: string): Promise<boolean> {
  return hasCommissionEntry({ dealId })
}

/**
 * Consume unrecouped expenses, oldest first, up to `amount`, and mark each
 * one recouped against the deal. Returns what was actually applied.
 */
async function applyRecoupment(
  expenses: AgentExpense[],
  agentId: string,
  clientId: string | null | undefined,
  amount: number,
  dealId: string | null | undefined,
): Promise<number> {
  if (amount <= 0) return 0
  let applied = 0
  for (const step of recoupmentPlan(expenses, agentId, clientId, amount)) {
    await recoupExpenseFromDeal(step.expenseId, dealId ?? '', step.take)
    applied += step.take
  }
  return applied
}

interface CommissionBasis {
  agentId: string
  base: number
  rate: number
  clientId: string | null | undefined
  dealId: string | null | undefined
}

async function computeCommission(basis: CommissionBasis) {
  const [settings, agentSplits, expenses] = await Promise.all([
    getAgencyCommissionSettings(),
    getAgentCommissionSplit(basis.agentId),
    listAgentExpenses({ agentId: basis.agentId }),
  ])

  const split = getEffectiveSplit(settings, agentSplits ? [agentSplits] : [], basis.agentId, new Date())
  if (!split) {
    throw new Error('No commission split is set for this agency or agent. Set the agency default under Agency defaults on the Commission page before commission can be booked.')
  }
  // The shared rule (shared/commissionModel): the nightly ledger job books
  // the same amounts from the same inputs.
  const unrecouped = unrecoupedTotal(expenses, basis.agentId, basis.clientId ?? null)
  const a = newLedgerAmounts(basis.base, basis.rate, split, unrecouped, Boolean(settings?.expense_recoupment_enabled))

  return {
    expenses, split,
    grossCommission: a.gross_amount, expensesDeducted: a.expenses_deducted, netCommission: a.net_amount,
    agentTake: a.agent_share, agencyTake: a.agency_share,
  }
}

/**
 * Keep an executed deal's ledger row equal to commission on the money that
 * has actually been paid on it.
 *
 * A deal that is not a playing contract (an endorsement, an appearance, an
 * NIL deal) pays out on a schedule. Until Sep 2026 execution booked rate ×
 * the whole deal value on day one. The row now follows the payment
 * schedule: paid items are earned, the rest are not yet. A deal with no
 * schedule at all is priced as before, on its full value, because that is
 * the only figure it carries, and the outcome says so.
 */
export async function reconcileDealCommission(deal: Deal): Promise<ReconcileOutcome> {
  if (deal.stage !== 'Executed') return { status: 'unknown', reason: `deal is ${deal.stage}` }
  if (!deal.commission_rate) return { status: 'unknown', reason: 'no commission rate on the deal' }

  const hasSchedule = Array.isArray(deal.payment_schedule) && deal.payment_schedule.length > 0
  const basis = dealBasis(deal)
  if (!basis) return { status: 'unknown', reason: 'no payment schedule and no value to price from' }

  const gross = grossCommission(basis.base, deal.commission_rate)
  const existing = await listCommissionLedger({ dealId: deal.id, transactionType: 'commission_earned' })
  const entry = existing[0]

  if (!entry) {
    if (gross <= 0) return { status: 'nothing_to_book' }
    const agentId = deal.assigned_agent_id || deal.user_id
    if (!agentId) throw new Error(`Deal "${deal.title}" has no assigned agent — cannot record commission`)
    const c = await computeCommission({
      agentId, base: basis.base, rate: deal.commission_rate, clientId: deal.client_id, dealId: deal.id,
    })
    await recordCommissionEarned(
      deal.id, agentId, c.grossCommission, c.expensesDeducted, c.netCommission,
      c.agentTake, c.agencyTake, c.split.agentPercentage, deal.client_id || undefined,
    )
    await applyRecoupment(c.expenses, agentId, deal.client_id, c.expensesDeducted, deal.id)
    return { status: 'created', base: basis.base, gross: c.grossCommission, basis: basis.basis }
  }

  if (sameMoney(entry.gross_amount, gross)) return { status: 'unchanged', gross }
  const amounts = reconciledAmounts(entry, gross)
  await updateCommissionLedgerEntry(entry.id, {
    ...amounts,
    notes: `Reconciled ${localDateString()} to ${hasSchedule ? 'paid schedule items' : 'deal value'} of $${basis.base.toLocaleString()} (was $${(entry.gross_amount ?? 0).toLocaleString()} commission).`,
  })
  return { status: 'updated', from: entry.gross_amount ?? 0, to: amounts.gross_amount, basis: basis.basis }
}

/**
 * Commission when a deal is executed. The same reconciliation every later
 * change to its payment schedule runs.
 */
export async function autoRecordDealCommission(deal: Deal): Promise<void> {
  await reconcileDealCommission(deal)
}

export type ReconcileOutcome =
  | { status: 'unknown'; reason: string }
  | { status: 'nothing_to_book' }
  | { status: 'created'; base: number; gross: number; basis: 'earned' | 'guaranteed' }
  | { status: 'unchanged'; gross: number }
  | { status: 'updated'; from: number; to: number; basis: 'earned' | 'guaranteed' }

/**
 * The money a contract has actually produced, for the ledger: what the
 * athlete has earned (utils/earnings, priced from roster periods and year
 * statuses), or the guaranteed money when nothing else is known yet. The
 * guarantee is contractually theirs at signing; the rest accrues.
 */
function commissionBasis(contract: Contract): { base: number; basis: 'earned' | 'guaranteed' } | null {
  return contractBasis(contract)
}

/**
 * Keep the contract's ledger row equal to commission on earned money.
 *
 * Until Sep 2026 activation booked rate × total paper value on day one, so
 * a player cut in October left the agency's fee income overstated until
 * someone edited the row. The row is now created against earned money (or
 * the guarantee, when that is all that is known) and re-priced whenever
 * the contract's earnings change: roster periods, year statuses, rate.
 * Expenses already recouped stay recouped; the split keeps its recorded
 * percentage. Nothing is written when the amounts already agree.
 */
export async function reconcileContractCommission(contract: Contract): Promise<ReconcileOutcome> {
  if (contract.status !== 'active') return { status: 'unknown', reason: `contract is ${contract.status}` }
  if (!contract.commission_rate) return { status: 'unknown', reason: 'no commission rate on the contract' }
  const basis = commissionBasis(contract)
  if (!basis) return { status: 'unknown', reason: 'no year statuses, roster periods or guaranteed value to price from' }

  const gross = grossCommission(basis.base, contract.commission_rate)
  const existing = [
    ...(await listCommissionLedger({ contractId: contract.id, transactionType: 'commission_earned' })),
    ...(contract.deal_id ? await listCommissionLedger({ dealId: contract.deal_id, transactionType: 'commission_earned' }) : []),
  ]
  const entry = existing.find(Boolean)

  if (!entry) {
    if (gross <= 0) return { status: 'nothing_to_book' }
    const agentId = contract.owner_id || contract.created_by
    if (!agentId) throw new Error(`Contract "${contract.title}" has no assigned agent — cannot record commission`)
    const c = await computeCommission({
      agentId, base: basis.base, rate: contract.commission_rate,
      clientId: contract.client_id, dealId: contract.deal_id,
    })
    await recordContractCommissionEarned(
      contract.id, agentId, c.grossCommission, c.expensesDeducted, c.netCommission,
      c.agentTake, c.agencyTake, c.split.agentPercentage, contract.client_id || undefined, contract.deal_id,
    )
    await applyRecoupment(c.expenses, agentId, contract.client_id, c.expensesDeducted, contract.deal_id)
    return { status: 'created', base: basis.base, gross: c.grossCommission, basis: basis.basis }
  }

  if (sameMoney(entry.gross_amount, gross)) return { status: 'unchanged', gross }
  const amounts = reconciledAmounts(entry, gross)
  await updateCommissionLedgerEntry(entry.id, {
    ...amounts,
    notes: `Reconciled ${localDateString()} to ${basis.basis === 'earned' ? 'earned' : 'guaranteed'} money of $${basis.base.toLocaleString()} (was $${(entry.gross_amount ?? 0).toLocaleString()} commission).`,
  })
  return { status: 'updated', from: entry.gross_amount ?? 0, to: amounts.gross_amount, basis: basis.basis }
}

/**
 * Commission when a contract is activated (or created active). Now the same
 * reconciliation every other change runs: books earned or guaranteed money,
 * never the paper total.
 */
export async function autoRecordContractCommission(contract: Contract): Promise<void> {
  await reconcileContractCommission(contract)
}
